import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantApi, License, OrganizationSubscription } from '@/api/tenant'
import { lcClassApi, LcClass } from '@/api/b2b'
import { contentApi } from '@/api/content'
import { commerceApi, ProductResponse, PricingResponse } from '@/api/commerce'
import * as configApi from '@/api/configuration'
import { usePageStore } from '@/store/pageStore'
import CurriculumTreeViewer from '@/components/common/CurriculumTreeViewer'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { ConfigurationSummary } from '@/components/configuration'
import {
  ChevronLeft,
  ChevronDown,
  Package,
  BookOpen,
  Users,
  Calendar as CalendarIcon,
  CheckCircle2,
  AlertCircle,
  Clock,
  Plus,
  ShoppingCart,
  Minus,
  GraduationCap,
  Settings,
  Info,
  Lock,
  Trash2,
} from 'lucide-react'
import { clsx } from 'clsx'
import { toast } from 'sonner'
import { getApiErrorMessage } from '@/utils/apiError'

export default function ProductDetail() {
  const { t } = useTranslation('admin')
  const { subscriptionId } = useParams<{ subscriptionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { setPageSubtitle } = usePageStore()

  // State
  const [subscriptions, setSubscriptions] = useState<OrganizationSubscription[]>([])
  const [licenses, setLicenses] = useState<License[]>([])
  const [products, setProducts] = useState<ProductResponse[]>([])
  const [currentSubscription, setCurrentSubscription] = useState<OrganizationSubscription | null>(null)
  const [currentLicense, setCurrentLicense] = useState<License | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Product selector dropdown
  const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false)

  // Extension modal
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false)
  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(null)
  const [extensionStartDate, setExtensionStartDate] = useState<string>('')

  // Expand seats modal
  const [isExpandSeatsModalOpen, setIsExpandSeatsModalOpen] = useState(false)
  const [additionalSeats, setAdditionalSeats] = useState<number>(10)

  // Tree viewer modal
  const [isTreeViewerOpen, setIsTreeViewerOpen] = useState(false)

  // Fetch classes to find which classes use this product (활성 클래스만)
  const { data: classesData } = useQuery({
    queryKey: ['lc-classes'],
    queryFn: () => lcClassApi.getAll(),
  })

  // Fetch curriculum hierarchy when tree viewer is opened
  const { data: hierarchyDataFromQuery, isLoading: isHierarchyLoading } = useQuery({
    queryKey: ['curriculum-hierarchy', currentSubscription?.curriculumContentId],
    queryFn: () => contentApi.getContentHierarchy(currentSubscription!.curriculumContentId!),
    enabled: isTreeViewerOpen && !!currentSubscription?.curriculumContentId,
  })

  // Product Configuration 조회 ({t('productDetail.configuration.readOnly')} - Super Admin이 설정)
  // 간소화된 계층: Product → Class (License는 Product 설정 상속)
  const { data: productConfiguration, isLoading: isProductConfigLoading } = useQuery({
    queryKey: ['configuration', 'product', currentSubscription?.productId],
    queryFn: () => configApi.getProductConfiguration(currentSubscription!.productId),
    enabled: !!currentSubscription?.productId,
  })

  // Default Configuration 조회 (Product에 설정이 없을 때 fallback)
  const { data: defaultConfiguration, isLoading: isDefaultConfigLoading } = useQuery({
    queryKey: ['configuration', 'default'],
    queryFn: () => configApi.getDefaultConfiguration(),
    staleTime: 10 * 60 * 1000, // 10분 캐시
  })

  const allClasses: LcClass[] = classesData?.data?.data || []
  
  // 현재 라이선스를 사용하는 클래스들 필터링
  // 참고: currentSubscription.id는 실제로 organization_licenses.license_id 입니다.
  // 백엔드 getOrganizationSubscriptions()가 license.id를 subscription.id로 반환합니다.
  // class_products 테이블의 license_id와 매칭됩니다.
  const classesUsingThisProduct = allClasses.filter(cls => 
    cls.licenses?.some(lic => lic.licenseId === currentSubscription?.id)
  )
  
  // 총 배정 인원 계산 (모든 클래스의 currentStudents 합계)
  const totalAssignedStudents = classesUsingThisProduct.reduce((sum, cls) => sum + cls.currentStudents, 0)

  // Set page subtitle
  useEffect(() => {
    setPageSubtitle(t('productDetail.subtitle'))
    return () => setPageSubtitle(null)
  }, [setPageSubtitle])

  // Load data on mount
  useEffect(() => {
    loadData()
  }, [])

  // Update current subscription when URL param changes
  useEffect(() => {
    if (subscriptions.length > 0 && subscriptionId) {
      const sub = subscriptions.find(s => s.id === parseInt(subscriptionId))
      if (sub) {
        setCurrentSubscription(sub)
        const license = licenses.find(l => l.productId === sub.productId)
        if (license) {
          setCurrentLicense(license)
        }
      }
    }
  }, [subscriptionId, subscriptions, licenses])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load subscriptions
      const subscriptionsResponse = await tenantApi.getSubscriptions()
      let activeSubs: OrganizationSubscription[] = []
      if (subscriptionsResponse.data.success) {
        const subs = subscriptionsResponse.data.data || []
        activeSubs = subs.filter(s => s.status === 'active')
        setSubscriptions(activeSubs)
      }

      // Load licenses (모든 활성 라이선스 - 구매 가능 + 구매한 것 모두)
      const licensesResponse = await tenantApi.getAllActiveLicenses()
      let licensesData: License[] = []
      if (licensesResponse.data.success) {
        licensesData = licensesResponse.data.data || []
        setLicenses(licensesData)
      }

      // currentSubscription과 currentLicense 업데이트
      if (subscriptionId && activeSubs.length > 0) {
        const updatedSub = activeSubs.find(s => s.id === parseInt(subscriptionId))
        if (updatedSub) {
          setCurrentSubscription(updatedSub)
          
          // 해당 subscription의 license 찾기
          const license = licensesData.find(l => l.productId === updatedSub.productId)
          if (license) {
            setCurrentLicense(license)
          }
        }
      }

      // Load products
      const productsData = await commerceApi.getProducts()
      setProducts(productsData)
    } catch (err: any) {
      console.error('[ProductDetail] Failed to load data:', err)
      setError(err.message || t('productDetail.toast.loadError'))
    } finally {
      setLoading(false)
    }
  }

  // Mutations
  const expandSeatsMutation = useMutation({
    mutationFn: ({ licenseId, additionalSeats }: { licenseId: number; additionalSeats: number }) =>
      tenantApi.expandSeats(licenseId, additionalSeats),
    onSuccess: async () => {
      setIsExpandSeatsModalOpen(false)
      setAdditionalSeats(10)

      // 좌석 정보 갱신을 위해 데이터 다시 로드
      await loadData()

      toast.success(t('productDetail.toast.seatsExpanded'))
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  const deleteSubscriptionMutation = useMutation({
    mutationFn: ({ subscriptionId, force }: { subscriptionId: number; force: boolean }) =>
      tenantApi.deleteSubscription(subscriptionId, force),
    onSuccess: () => {
      toast.success(t('productDetail.toast.deleted'))
      navigate('/admin/products')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, t))
    },
  })

  // Handlers
  const handleProductSelect = (sub: OrganizationSubscription) => {
    setIsProductSelectorOpen(false)
    navigate(`/admin/products/${sub.id}`)
  }

  const handleExtension = async () => {
    if (!currentSubscription || !selectedPricingId || !extensionStartDate) {
      toast.warning(t('productDetail.toast.selectAllRequired'))
      return
    }

    const selectedPricing = products
      .find((p: ProductResponse) => p.id === currentSubscription.productId)
      ?.pricingOptions?.find((p: PricingResponse) => p.id === selectedPricingId)

    if (!selectedPricing) {
      toast.error(t('productDetail.toast.pricingNotFound'))
      return
    }

    if (!confirm(t('productDetail.confirmExtension', {
      price: formatPrice(selectedPricing.price, selectedPricing.currency),
      startDate: formatDate(extensionStartDate)
    }))) {
      return
    }

    try {
      // Create order for extension
      const order = await commerceApi.createOrder({
        productId: currentSubscription.productId,
        pricingId: selectedPricingId,
        quantity: 1,
      })

      // Process payment
      await commerceApi.processPayment({
        orderId: order.id,
        paymentMethod: 'credit_card',
        cardNumber: '4242424242424242',
        validFrom: extensionStartDate,
      })

      toast.success(t('productDetail.toast.extensionSuccess', { orderNumber: order.orderNumber }))
      setIsExtensionModalOpen(false)
      resetExtensionForm()
      await loadData()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, t))
    }
  }

  const resetExtensionForm = () => {
    setSelectedPricingId(null)
    setExtensionStartDate('')
  }

  // Helpers
  const formatPrice = (price: number, currency: string = 'KRW') => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: currency,
    }).format(price)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getStatusBadge = (subscription: OrganizationSubscription) => {
    const now = new Date()
    const validFrom = new Date(subscription.validFrom)
    const validUntil = subscription.validUntil ? new Date(subscription.validUntil) : null

    if (now < validFrom) {
      return { label: t('productDetail.status.pending'), icon: Clock, className: 'bg-yellow-100 text-yellow-700' }
    }

    if (validUntil && now > validUntil) {
      return { label: t('productDetail.status.expired'), icon: AlertCircle, className: 'bg-red-100 text-red-700' }
    }

    // Check if expiring soon (within 30 days)
    if (validUntil) {
      const daysUntilExpiry = Math.ceil((validUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      if (daysUntilExpiry <= 30) {
        return { label: t('productDetail.status.expiringSoon', { days: daysUntilExpiry }), icon: AlertCircle, className: 'bg-orange-100 text-orange-700' }
      }
    }

    return { label: t('productDetail.status.active'), icon: CheckCircle2, className: 'bg-green-100 text-green-700' }
  }

  const currentProduct = products.find(p => p.id === currentSubscription?.productId)

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    )
  }

  // No subscription selected
  if (!currentSubscription) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/admin/products')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t('productDetail.backToProducts')}
        </button>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('productDetail.noProduct')}</p>
        </div>
      </div>
    )
  }

  const statusBadge = getStatusBadge(currentSubscription)
  const StatusIcon = statusBadge.icon

  return (
    <div className="space-y-6">
      {/* Header with back button and product selector */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/products')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t('productDetail.backToProducts')}
        </button>

        {/* Product Selector Dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsProductSelectorOpen(!isProductSelectorOpen)}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Package className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-gray-700">
              {currentSubscription.productName || t('productDetail.productNumber', { id: currentSubscription.productId })}
            </span>
            <ChevronDown className={clsx(
              "w-4 h-4 text-gray-400 transition-transform",
              isProductSelectorOpen && "rotate-180"
            )} />
          </button>

          {isProductSelectorOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsProductSelectorOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                <div className="p-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500 px-2">{t('productDetail.selectProduct')}</p>
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {subscriptions.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => handleProductSelect(sub)}
                      className={clsx(
                        "w-full flex items-center p-3 rounded-lg text-left transition-colors",
                        sub.id === currentSubscription.id
                          ? "bg-primary-50 border border-primary-200"
                          : "hover:bg-gray-50"
                      )}
                    >
                      <Package className="w-5 h-5 text-gray-400 mr-3" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {sub.productName || t('productDetail.productNumber', { id: sub.productId })}
                        </p>
                        <p className="text-xs text-gray-500">
                          {sub.curriculumTitle || t('productDetail.curriculumNotLinked')}
                        </p>
                      </div>
                      {sub.id === currentSubscription.id && (
                        <CheckCircle2 className="w-5 h-5 text-primary-600 ml-2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Product Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Product Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {currentSubscription.productName || t('productDetail.productNumber', { id: currentSubscription.productId })}
              </h1>
              {currentSubscription.curriculumTitle && (
                <div className="flex items-center mt-2 text-primary-100">
                  <BookOpen className="w-4 h-4 mr-2" />
                  <span>{currentSubscription.curriculumTitle}</span>
                  {currentSubscription.curriculumContentId && (
                    <button
                      onClick={() => setIsTreeViewerOpen(true)}
                      className="ml-3 px-3 py-1 text-sm font-medium bg-white/30 hover:bg-white/50 border border-white/40 rounded-lg shadow-sm transition-all hover:scale-105"
                    >
                      📂 {t('productDetail.viewStructure')}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className={clsx(
                "flex items-center px-3 py-1 rounded-full text-sm font-medium",
                statusBadge.className
              )}>
                <StatusIcon className="w-4 h-4 mr-1" />
                {statusBadge.label}
              </div>
              <button
                onClick={async () => {
                  // 이 상품을 사용하는 클래스가 있는지 먼저 확인
                  const usingClassCount = classesUsingThisProduct.length

                  if (usingClassCount > 0) {
                    const forceDelete = confirm(t('productDetail.delete.confirmWithClasses', {
                      count: usingClassCount,
                      classes: classesUsingThisProduct.map(c => c.name).join(', ')
                    })
                    )
                    if (forceDelete) {
                      deleteSubscriptionMutation.mutate({
                        subscriptionId: currentSubscription.id,
                        force: true
                      })
                    }
                  } else {
                    const confirmDelete = confirm(t('productDetail.delete.confirmSimple', {
                      name: currentSubscription.productName
                    }))
                    if (confirmDelete) {
                      deleteSubscriptionMutation.mutate({
                        subscriptionId: currentSubscription.id,
                        force: false
                      })
                    }
                  }
                }}
                disabled={deleteSubscriptionMutation.isPending}
                className="flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {deleteSubscriptionMutation.isPending ? t('productDetail.delete.deleting') : t('productDetail.delete.button')}
              </button>
            </div>
          </div>

          {/* Classes using this product - 헤더 내부에 배치 */}
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex items-center text-sm text-primary-100 mb-2">
              <GraduationCap className="w-4 h-4 mr-1.5" />
              <span>{t('productDetail.usingClasses', { count: classesUsingThisProduct.length })}</span>
            </div>
            {classesUsingThisProduct.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {classesUsingThisProduct.map((cls) => (
                  <button
                    key={cls.id}
                    onClick={() => navigate('/admin/classes', { 
                      state: { selectClassId: cls.id }
                    })}
                    className="inline-flex items-center px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium text-white transition-colors"
                  >
                    {cls.name}
                    <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                      {cls.currentStudents}
                    </span>
                  </button>
                ))}
                <button
                  onClick={() => navigate('/admin/classes', { 
                    state: { 
                      openCreate: true, 
                      licenseId: currentSubscription.id,
                      productName: currentSubscription.productName
                    } 
                  })}
                  className="inline-flex items-center px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full text-sm font-medium text-white/80 hover:text-white transition-colors"
                >
                  {t('productDetail.addClass')}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <p className="text-sm text-primary-200">{t('productDetail.noClasses')}</p>
                <button
                  onClick={() => navigate('/admin/classes', { 
                    state: { 
                      openCreate: true, 
                      licenseId: currentSubscription.id,
                      productName: currentSubscription.productName
                    } 
                  })}
                  className="text-xs px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors"
                >
                  {t('productDetail.createClass')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Product Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-200">
          <div className="p-6 text-center">
            <p className="text-3xl font-bold text-gray-900">{totalAssignedStudents}</p>
            <p className="text-sm text-gray-500 mt-1">{t('productDetail.stats.totalAssigned')}</p>
          </div>
          <div className="p-6 text-center">
            <p className="text-3xl font-bold text-gray-900">
              {currentSubscription.purchasedSeats === 0 ? t('productDetail.stats.unlimited') : currentSubscription.purchasedSeats}
            </p>
            <p className="text-sm text-gray-500 mt-1">{t('productDetail.stats.purchasedSeats')}</p>
          </div>
          <div className="p-6 text-center">
            <p className="text-sm font-medium text-gray-900">{formatDate(currentSubscription.validFrom)}</p>
            <p className="text-sm text-gray-500 mt-1">{t('productDetail.stats.startDate')}</p>
          </div>
          <div className="p-6 text-center">
            <p className="text-sm font-medium text-gray-900">
              {currentSubscription.validUntil ? formatDate(currentSubscription.validUntil) : t('productDetail.stats.noExpiry')}
            </p>
            <p className="text-sm text-gray-500 mt-1">{t('productDetail.stats.expiryDate')}</p>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* {t('productDetail.extension.title')} 카드 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mr-3">
              <CalendarIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('productDetail.extension.title')}</h3>
              <p className="text-sm text-gray-500">{t('productDetail.extension.description')}</p>
            </div>
          </div>
          <div className="text-sm mb-4">
            <div className="flex justify-between mb-2">
              <span className="text-gray-500">{t('productDetail.extension.currentExpiry')}</span>
              <span className="font-medium text-gray-900">
                {currentSubscription.validUntil ? formatDate(currentSubscription.validUntil) : t('productDetail.extension.noExpiry')}
              </span>
            </div>
            <p className="text-gray-600">
              {t('productDetail.extension.extendNote')}
            </p>
          </div>
          <button
            onClick={() => {
              setIsExtensionModalOpen(true)
              // 기본 시작일을 현재 만료일 다음날로 설정
              if (currentSubscription.validUntil) {
                const nextDay = new Date(currentSubscription.validUntil)
                nextDay.setDate(nextDay.getDate() + 1)
                setExtensionStartDate(nextDay.toISOString().split('T')[0])
              }
            }}
            disabled={!currentSubscription.validUntil}
            className="w-full py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t('productDetail.extension.extendButton')}
          </button>
          {!currentSubscription.validUntil && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              {t('productDetail.extension.noExpiryNote')}
            </p>
          )}
        </div>

        {/* {t('productDetail.seats.title')} 카드 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center mr-3">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('productDetail.seats.title')}</h3>
              <p className="text-sm text-gray-500">{t('productDetail.seats.description')}</p>
            </div>
          </div>
          <div className="space-y-2 text-sm text-gray-600 mb-4">
            <div className="flex justify-between">
              <span>{t('productDetail.seats.currentSeats')}</span>
              <span className="font-medium">
                {currentSubscription.purchasedSeats === 0 ? t('productDetail.seats.unlimitedSeats') : t('productDetail.seats.seatCount', { count: currentSubscription.purchasedSeats })}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t('productDetail.seats.inUse')}</span>
              <span className="font-medium">{t('productDetail.seats.seatCount', { count: totalAssignedStudents })}</span>
            </div>
          </div>
          <button
            onClick={() => setIsExpandSeatsModalOpen(true)}
            disabled={currentSubscription.purchasedSeats === 0}
            className="w-full py-2 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('productDetail.seats.addButton')}
          </button>
          {currentSubscription.purchasedSeats === 0 && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              {t('productDetail.seats.noExpansionNeeded')}
            </p>
          )}
        </div>
      </div>

      {/* Product Configuration Section ({t('productDetail.configuration.readOnly')}) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center mr-3">
              <Settings className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('productDetail.configuration.title')}</h3>
              <p className="text-sm text-gray-500">{t('productDetail.configuration.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
            <Lock className="w-3 h-3" />
            {t('productDetail.configuration.readOnly')}
          </div>
        </div>

        {/* 설정 소스 안내 메시지 */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">{t('productDetail.configuration.superAdminManaged')}</p>
              <p className="mt-1 text-blue-600">{t('productDetail.configuration.classCustomize')}</p>
            </div>
          </div>
        </div>

        <ConfigurationSummary
          configuration={productConfiguration || defaultConfiguration || null}
          isLoading={isProductConfigLoading || isDefaultConfigLoading}
        />
      </div>

      {/* {t('productDetail.extension.title')} 모달 */}
      <Dialog open={isExtensionModalOpen} onOpenChange={(open) => { if (!open) { setIsExtensionModalOpen(false); resetExtensionForm() } }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <div>{t('productDetail.extension.title')}</div>
            <p className="text-sm text-gray-500 mt-1 font-normal">
              {currentSubscription.productName}
            </p>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* 현재 상태 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{t('productDetail.extension.currentStatus')}</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t('productDetail.extension.startDate')}</span>
                <span className="ml-2 font-medium">{formatDate(currentSubscription.validFrom)}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('productDetail.extension.expiryDate')}</span>
                <span className="ml-2 font-medium">
                  {currentSubscription.validUntil ? formatDate(currentSubscription.validUntil) : t('productDetail.extension.noExpiry')}
                </span>
              </div>
            </div>
          </div>

          {/* 연장 시작일 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('productDetail.extension.extensionStartDate')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={extensionStartDate}
              onChange={(e) => setExtensionStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('productDetail.extension.extensionStartDateNote')}
            </p>
          </div>

          {/* 가격 옵션 선택 */}
          {currentProduct?.pricingOptions && currentProduct.pricingOptions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('productDetail.extension.selectPeriod')} <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {currentProduct.pricingOptions.map((pricing: PricingResponse) => (
                  <label
                    key={pricing.id}
                    className={clsx(
                      "block p-4 border-2 rounded-lg cursor-pointer transition-all",
                      selectedPricingId === pricing.id
                        ? "border-primary-600 bg-primary-50"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="extension-pricing"
                        value={pricing.id}
                        checked={selectedPricingId === pricing.id}
                        onChange={() => setSelectedPricingId(pricing.id)}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900">{pricing.name}</span>
                          <span className="text-xl font-bold text-primary-600">
                            {formatPrice(pricing.price, pricing.currency)}
                          </span>
                        </div>
                        {pricing.billingPeriod && (
                          <span className="text-sm text-gray-500">
                            / {pricing.billingPeriod === 'monthly' ? t('product.billingPeriod.monthly') :
                              pricing.billingPeriod === 'yearly' ? t('product.billingPeriod.yearly') :
                              pricing.billingPeriod === 'quarterly' ? t('product.billingPeriod.quarterly') :
                              pricing.billingPeriod}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={() => { setIsExtensionModalOpen(false); resetExtensionForm() }}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('productDetail.extension.cancel')}
          </button>
          <button
            onClick={handleExtension}
            disabled={!selectedPricingId || !extensionStartDate}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t('productDetail.extension.pay')}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* {t('productDetail.seats.title')} 모달 */}
      <Dialog open={isExpandSeatsModalOpen} onOpenChange={setIsExpandSeatsModalOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div>{t('productDetail.seats.title')}</div>
            <p className="text-sm text-gray-500 mt-1 font-normal">
              {currentSubscription.productName}
            </p>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* 현재 상태 */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('productDetail.seats.currentSeats')}</span>
              <span className="font-medium text-gray-900">{t('productDetail.seats.seatCount', { count: currentSubscription.purchasedSeats })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('productDetail.seats.inUse')}</span>
              <span className="font-medium text-gray-900">{t('productDetail.seats.seatCount', { count: totalAssignedStudents })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('productDetail.seats.available')}</span>
              <span className="font-medium text-green-600">
                {t('productDetail.seats.seatCount', { count: currentSubscription.purchasedSeats - currentSubscription.usedSeats })}
              </span>
            </div>
          </div>

          {/* 추가 좌석 입력 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('productDetail.seats.additionalLabel')}
            </label>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setAdditionalSeats(Math.max(1, additionalSeats - 10))}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <Minus className="w-5 h-5" />
              </button>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={additionalSeats === 0 ? '' : additionalSeats}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '')
                  setAdditionalSeats(val === '' ? 0 : parseInt(val, 10))
                }}
                className="flex-1 text-center text-xl font-bold py-2 border border-gray-300 rounded-lg"
              />
              <button
                onClick={() => setAdditionalSeats(additionalSeats + 10)}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* 결과 미리보기 */}
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="flex justify-between text-sm">
              <span className="text-orange-700">{t('productDetail.seats.totalAfter')}</span>
              <span className="font-bold text-orange-700">
                {t('productDetail.seats.seatCount', { count: currentSubscription.purchasedSeats + additionalSeats })}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => setIsExpandSeatsModalOpen(false)}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('productDetail.seats.cancel')}
          </button>
          <button
            onClick={() => {
              if (currentLicense) {
                expandSeatsMutation.mutate({
                  licenseId: currentLicense.id,
                  additionalSeats: additionalSeats
                })
              }
            }}
            disabled={expandSeatsMutation.isPending}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            {expandSeatsMutation.isPending ? t('productDetail.seats.processing') : t('productDetail.seats.addSeats', { count: additionalSeats })}
          </button>
        </DialogFooter>
      </DialogContent>
      </Dialog>

      {/* 커리큘럼 트리 뷰어 */}
      {hierarchyDataFromQuery && (
        <CurriculumTreeViewer
          data={hierarchyDataFromQuery}
          isOpen={isTreeViewerOpen}
          onClose={() => setIsTreeViewerOpen(false)}
          title={currentSubscription.curriculumTitle || t('curriculum.detail')}
        />
      )}

      {/* 트리 뷰어 로딩 중 */}
      <Dialog open={isTreeViewerOpen && isHierarchyLoading} onOpenChange={(open) => { if (!open) setIsTreeViewerOpen(false) }}>
        <DialogContent className="sm:max-w-xs">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-700">{t('productDetail.configuration.curriculumLoading')}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}











